import { Labels } from '../global/labels';
import {
    nextByCond,
    previousByCond,
    closestByCond,
    closestEventHolder,
    closestTag,
    createElement,
    defineElement,
    detachChildNodes,
    dispatchCustomEvent,
    dispatchNativeEvent,
    focusInteractiveElement,
    getAttributes,
    getChildrenForSlot,
    getFocusableElements,
    hide,
    insertElements,
    isDOMReady,
    isParentOf,
    isVisible,
    mergeAttributes,
    position,
    queryChildren,
    redetachChildNodes,
    revertClassList,
    runTransition,
    setAttributes,
    setInnerText,
    show,
    updateClassList,
    updateElement,
    whenDOMReady,
    mutateAttributesTo,
    mutateChildrenTo,
    mutateTo,
    prerenderElementsTree,
    rebuild,
    rebuildChildren,
    runSmoothRemove,
    runSmoothAppend,
    tagRegistry,
    setDebugMode,
    getDebugMode,
    getRenderMode,
    RenderMode,
    setRenderMode,
    nameConverter,
    setUnsafeInnerHTML,
} from '../global/ui-helpers';
import type {
    SmoothRemoveCallback,
    SmoothAppendCallback,
} from '../global/mutations/index';
import type {
    AttributeValue,
    AttributeType,
    Props,
    ChildSelector,
    ElementConfig,
    LifeCycleHook,
    AttributeValidationHandler,
    ComponentPlugin,
    PropsAttribute,
    ComponentStyles,
    SymbolicProps,
    LifeCycle,
    Attributes,
    AllowedTagName,
} from './ui-element.type';
import type { Locale, Language } from '../global/enums';
import {
    patchExternallyControlledFormProperties,
    isCustomFormComponent,
    resolveChildDescriptor,
    resolvePropertyDescriptor,
    twoWayBinding,
} from './ui-element.helper';
import { ComponentState } from './ui-element.const';
import { Attribute } from '../decorators/attribute.decorator';
import { Deprecated } from '../decorators/deprecated.decorator';
import { Component } from '../decorators/component.decorator';
import type { TranslationLabels } from '../types/labels.type';
import { isPlainObject } from '../global/helpers';
import type {
    EventHandler,
    HTMLElementOrParent,
    HTMLInputSelectOrTextElement,
    MaybeHTMLElement,
    MaybeHTMLElementOrParent,
    Optional,
    PredicateFn,
} from '../global/utils.type';

/**
 * @class
 * @memberof SharedComponents
 * @augments {HTMLElement}
 * @alias UIElement
 * @element ui-element
 * @classdesc Core interface for classes that represent a Custom Element.
 * Don't use this element, this is only for testing purposes mostly.
 * This can be used as "smart" div, if you know exactly what are you doing.
 * Methods of this class are inherited by all other UI components.<br>
 *   <ui-message type="urgent">
 *     You should never change state attribute <b>state=""</b> manually!
 *   </ui-message>
 *   <ui-message type="warning">
 *     In the most cases you should not create <b>&lt;ui-element></b>.
 *   </ui-message>
 * @property {boolean} rendered Shows if the component is in rendered state.
 * @property {boolean} hydrated Shows if the component is in hydrated state.
 * @property {string} plugins {@attr plugins} Space-separated plugins for component.
 * @property {Props} props - Component attribute, children bindings.
 * True for all components at this version.
 */
@Component({ tag: 'ui-element' })
export class UIElement extends HTMLElement implements SymbolicProps, LifeCycle {
    /* SymbolicProps implementation */
    [key: symbol]: AttributeValue;

    protected subscriptions: Record<
        string,
        Record<string, Array<EventHandler>>
    > = {}; // Deprecated
    protected static labels: TranslationLabels.Labels = {};
    protected static plugins: Record<string, typeof ComponentPlugin>;
    protected static autoloadPlugins: Record<string, typeof ComponentPlugin>;
    protected instancePlugins!: Record<string, ComponentPlugin>;
    protected static _props: Props = {
        attributes: {},
        children: {},
    };
    private static propsToValidate: Array<string> = [];
    private static readonly patchedControlElements =
        new WeakSet<HTMLInputSelectOrTextElement>();

    /**
     * Gets the WeakSet of patched control elements.
     * Used internally and for testing purposes.
     * @returns {WeakSet<HTMLInputSelectOrTextElement>}
     * @protected
     */
    protected static getPatchedControlElements(): WeakSet<HTMLInputSelectOrTextElement> {
        return this.patchedControlElements;
    }

    @Attribute(String)
    state!: ComponentState;

    /**
     * Provides list of observed attributes to be watched.
     * By default, all declared attributes of the component, but can be
     * overridden by subclass.
     */
    static get observedAttributes(): Array<string> {
        return this.getObservedAttributesMapping()
            .filter(Boolean)
            .map((attribute) => String(attribute.name));
    }

    /**
     * @deprecated Use \@Attribute, \@Children, \@Child decorators instead.
     */
    static get props(): Props {
        if (!this._props) {
            return {
                attributes: {},
                children: {},
            };
        }
        return {
            attributes: this._props.attributes || {},
            children: this._props.children || {},
        };
    }

    static set props(props: Props) {
        this._props = props;
    }

    /**
     * Locale for UIElements. Possible values are: en, ru, ee, lv, lt.
     * @type {Locale}
     */
    static get LOCALE(): Locale {
        return Labels.LOCALE;
    }

    static set LOCALE(value: Locale) {
        Labels.LOCALE = value;
    }

    /**
     * Debug mode, default is false.
     * @type {boolean}
     */
    @Deprecated(`Use config.getDebugMode()`)
    static get DEBUG_MODE(): boolean {
        return !!getDebugMode();
    }

    /**
     * @param {boolean} value
     */
    @Deprecated(`Use config.setDebugMode(true)`)
    static set DEBUG_MODE(value: boolean) {
        setDebugMode(value);
    }

    /**
     * Rendering mode, default is synchronous.
     * @type {boolean}
     */
    static get ASYNC_MODE(): boolean {
        return getRenderMode() === RenderMode.Asynchronous;
    }

    /**
     * @param {boolean} value
     */
    @Deprecated(`Use config.setRenderMode(RenderMode.Asynchronous);`)
    static set ASYNC_MODE(value) {
        setRenderMode(value ? RenderMode.Asynchronous : RenderMode.Synchronous);
    }

    /**
     * Language for UIElements. Possible values are: en, et, lv, lt, ru.
     * @type {Language}
     */
    static get LANGUAGE(): Language {
        return Labels.LANGUAGE;
    }

    static set LANGUAGE(value) {
        Labels.LANGUAGE = value;
    }

    /**
     * Checks if document is ready for Web Components to be rendered.
     * @deprecated use UI.isDOMReady()
     * @returns {boolean}
     */
    static get isDOMReady(): typeof isDOMReady {
        return isDOMReady;
    }

    /**
     * Adds DOMLoaded handler.
     * @returns {function}
     */
    @Deprecated(`Use UI.whenDOMReady()`)
    static get whenDOMReady(): typeof whenDOMReady {
        return whenDOMReady;
    }

    /**
     * Defines attribute on component construct.
     * @param {string} name
     * @param {AttributeType} type
     * @param {string | number | boolean} defaultValue
     * @param {string} [propertyName]
     * @param {boolean} [reflect]
     * @returns {UIElement}
     */
    static defineAttribute(
        name: string,
        type: AttributeType,
        defaultValue: AttributeValue,
        propertyName?: string,
        reflect: boolean = true
    ): typeof UIElement {
        const formattedPropertyName = String(
            nameConverter.attrToProperty(propertyName || name)
        );
        const descriptor = resolvePropertyDescriptor(
            name,
            type,
            defaultValue,
            reflect
        );
        const ownDescriptor = Object.getOwnPropertyDescriptor(
            this.prototype,
            formattedPropertyName
        );
        if (ownDescriptor?.get) {
            descriptor.get = ownDescriptor.get;
        }
        if (ownDescriptor?.set) {
            const newSetter = descriptor.set;
            descriptor.set = function (
                this: UIElement,
                value: AttributeValue
            ): void {
                // Set property if values are different, otherwise do nothing.
                if (this[Symbol.for(formattedPropertyName)] !== value) {
                    newSetter?.call(this, value);
                    ownDescriptor.set?.call(this, value);
                }
            };
        }

        twoWayBinding(this, {
            name,
            type,
            propertyName,
            default: defaultValue,
            reflect,
        });
        Object.defineProperty(
            this.prototype,
            formattedPropertyName,
            descriptor
        );
        return this;
    }

    /**
     * Defines child shortcut on component construct.
     * @param {string} propertyName - name of property
     * @param {string} selector
     * @param {null} [__fallback] - Deprecated: this keeps backwards compatibility where
     * defineChild signature with used with legacy fallback.
     * @param {boolean} [multiple] - targeting multiple nodes.
     * @returns {UIElement}
     */
    @Deprecated(`This method is strongly deprecated and exists only for backwards
     compatibility of the long time ago create project. You should not ever use it,
     it will be removed! Define children in props instead. This method stays only for 
     internal usage and will be private.`)
    static defineChild(
        propertyName: string,
        selector: ChildSelector,
        __fallback: null,
        multiple: boolean = false
    ): typeof UIElement {
        const descriptor = resolveChildDescriptor(
            propertyName,
            selector,
            multiple
        );
        Object.defineProperty(this.prototype, propertyName, descriptor);
        return this;
    }

    /**
     * Defines children shortcut on component construct.
     * @param {string} propertyName - name of property
     * @param {string} selector
     * @returns {UIElement}
     */
    @Deprecated(`This method is strongly deprecated and exists only for backwards
     compatibility of the long time ago create project. You should not ever use it,
     it will be removed! Define children in props instead. This method stays only for 
     internal usage and will be private.`)
    static defineChildren(
        propertyName: string,
        selector: ChildSelector
    ): typeof UIElement {
        return this.defineChild(propertyName, selector, null, true);
    }

    /**
     * Defines abstract element.
     * Used for creating abstract elements that can be used as a base for other elements.
     * @param {Props} [props] Define properties for the element.
     * @returns {UIElement}
     */
    static defineAbstractElement(props: Props = this.props): typeof UIElement {
        if (typeof props === 'object') {
            const attributes = (props || this.props).attributes || {};
            const children = (props || this.props).children || {};
            for (const key in attributes) {
                if (Object.hasOwn(attributes, key)) {
                    const attribute = attributes[key];
                    let name = nameConverter.propToAttribute(key);
                    let type: AttributeType = String;
                    let defaultValue: AttributeValue = null;
                    let validate = null;
                    let reflect;
                    if (typeof attribute === 'object' && attribute !== null) {
                        if (attribute.name) {
                            name = attribute.name;
                        }
                        if (attribute.type) {
                            type = attribute.type;
                        }
                        defaultValue =
                            attribute.default !== undefined
                                ? attribute.default
                                : defaultValue;
                        validate = attribute.validate;
                        reflect = attribute.reflect !== false;
                    } else {
                        type = attribute;
                    }
                    if (validate) {
                        this.propsToValidate.push(name);
                    }
                    this.defineAttribute(
                        name,
                        type,
                        defaultValue,
                        key,
                        reflect
                    );
                }
            }
            for (const key in children) {
                if (Object.hasOwn(children, key)) {
                    const child = children[key];
                    let selector: ChildSelector;
                    let multiple = false;
                    if (typeof child === 'object' && child !== null) {
                        selector = child.selector;
                        multiple = !!child.multiple;
                    } else {
                        selector = child;
                    }
                    this.defineChild(key, selector, null, multiple);
                }
            }
        }
        return this;
    }

    /**
     * @private
     * @returns {PropsAttribute[]}
     */
    private static getObservedAttributesMapping(): Array<PropsAttribute> {
        if (!this.props) {
            return [];
        }
        return Object.getOwnPropertyNames(this.props.attributes || {}).reduce<
            Array<PropsAttribute>
        >((collection, propertyName) => {
            if (!this.props.attributes) {
                return collection;
            }
            let property = this.props.attributes[propertyName];
            if (!property) {
                return collection;
            }

            let name;
            let type;
            let reflect = true;
            let defaultValue;
            if (isPlainObject(property)) {
                property = property as PropsAttribute;
                name = nameConverter.propToAttribute(
                    property.name || propertyName
                );
                // String is always default type for the attributes.
                type = property.type || String;
                reflect = property.reflect !== false;
                defaultValue = property.default;
            } else {
                name = nameConverter.propToAttribute(propertyName);
                type = property as AttributeType;
            }

            const newAttribute: PropsAttribute = {
                propertyName,
                name,
                reflect,
                type,
                default: defaultValue,
            };
            collection.push(newAttribute);
            return collection;
        }, []);
    }

    /**
     * Defines custom element
     * @param {string} tag
     * @param {string[] | string} [styles]
     * @returns {UIElement}
     */
    protected static defineElement(
        tag: string,
        styles?: ComponentStyles
    ): typeof UIElement {
        this.defineAbstractElement();
        const stylePlacement = document.getElementById(
            'shared-components-style'
        );
        defineElement(tag, this, { styles, stylePlacement });
        return this;
    }

    /**
     * Defines property on component construct.
     * @param {string | symbol} propertyName - name of property
     * @param {PropertyDescriptor} descriptor
     * @returns {UIElement}
     */
    @Deprecated(`This method is strongly deprecated and exists only for backwards
     compatibility of the long time ago create project. You should not ever use it,
     it will be removed! Define properties in props instead.`)
    protected static defineProperty(
        propertyName: string | symbol,
        descriptor: PropertyDescriptor
    ): typeof UIElement {
        Object.defineProperty(this.prototype, propertyName, descriptor);
        return this;
    }

    /**
     * Merge two attributes objects.
     * @returns {AttributeProps | NamedNodeMap}
     */
    @Deprecated(`Use UI.mergeAttributes()`)
    protected static get mergeAttributes(): typeof mergeAttributes {
        return mergeAttributes;
    }

    /**
     * Smoothly remove node from DOM tree.
     * @returns {Promise<void>}
     */
    @Deprecated(`Use UI.runSmoothRemove()`)
    protected static get runSmoothRemove(): typeof runSmoothRemove {
        return runSmoothRemove;
    }

    /**
     * Smoothly append the NODE to target element.
     * @returns {Promise<void>}
     */
    @Deprecated(`Use UI.runSmoothAppend()`)
    protected static get runSmoothAppend(): typeof runSmoothAppend {
        return runSmoothAppend;
    }

    /**
     * Mutates children object.
     * @returns {function}
     */
    @Deprecated(`Use UI.mutateChildrenTo()`)
    protected static get mutateChildrenTo(): typeof mutateChildrenTo {
        return mutateChildrenTo;
    }

    /**
     * Mutates attributes object.
     * @returns {function}
     */
    @Deprecated(`Use UI.mutateAttributesTo()`)
    protected static get mutateAttributesTo(): typeof mutateAttributesTo {
        return mutateAttributesTo;
    }

    /**
     * It will create a path to mutate old node element to new node.
     * @returns {function}
     */
    @Deprecated(`Use UI.mutateTo()`)
    protected static get mutateTo(): typeof mutateTo {
        return mutateTo;
    }

    /**
     * Gets element tag name.
     * @returns {string}
     */
    static get is(): string | undefined {
        return tagRegistry.get(this);
    }

    get rendered(): boolean {
        return [ComponentState.Rendered, ComponentState.Hydrated].includes(
            this.state
        );
    }

    get hydrated(): boolean {
        return this.state === ComponentState.Hydrated;
    }

    /**
     * Checks if the element is parent of given node.
     * @param {Element} node - given node
     * @returns {boolean} - true if current node is parent to given node
     */
    isParentOf(node: Element): boolean {
        return isParentOf(this, node);
    }

    /**
     * Checks if the element is visible.
     * @returns {boolean} true if visible
     */
    isVisible(): boolean {
        return isVisible(this);
    }

    /**
     * Returns list of subscriptions for given target and event name.
     * @private
     * @param {string} target - child selector or :root by default for self
     * @param {string} eventName - the event name
     * @returns {Array<EventHandler>} list of callbacks are bond to the event
     */
    @Deprecated()
    private getSubscriptions(
        target: string,
        eventName: string
    ): Array<EventHandler> {
        const key = !target ? ':root' : target;
        if (!this.subscriptions[key]) {
            this.subscriptions[key] = {};
        }
        if (!this.subscriptions[key][eventName]) {
            this.subscriptions[key][eventName] = [];
        }
        return this.subscriptions[key][eventName];
    }

    /**
     * return list of the node's attributes.
     * @returns {Record<string, string | number | boolean | null>}
     */
    getAttributes(): Attributes {
        return getAttributes(this);
    }

    /**
     * @param {Record<string, string | null>} attributes to be applied to the node
     * @example
     * // Set attributes "foo" and "bar" to attributes "id" and "name" respectively
     * element.setAttributes({id: "foo", name: "bar"});
     * @returns {UIElement | HTMLElement}
     */
    setAttributes(attributes: Attributes): Element {
        return setAttributes(this, attributes);
    }

    /**
     * Remove class -hidden to show the element.
     * @returns {UIElement | HTMLElement}
     */
    show(): HTMLElement {
        return show(this);
    }

    /**
     * Add class -hidden to hide the element.
     * @returns {UIElement | HTMLElement}
     */
    hide(): HTMLElement {
        return hide(this);
    }

    /**
     * Focus first interactive element inside of the node.
     * @param {boolean} skipPriorities
     * @returns {UIElement | Element}
     */
    focusInteractiveElement(skipPriorities: boolean): Element {
        return focusInteractiveElement(this, skipPriorities);
    }

    /**
     * Return list of focusable elements
     * @returns {Array<HTMLElement>}
     */
    getFocusableElements(): Array<HTMLElement> {
        return getFocusableElements(this);
    }

    /**
     * Search the closest parent node matched by given predicate.
     * @example
     * // Search closest parent node with "id" equals "foo"
     * element.closestByCond(function (node) {
     *     return node.id === "id"
     * });
     * @param {PredicateFn<HTMLElementOrParent>} searchPredicate - the callback should return true value if node is matched
     * @param {PredicateFn<HTMLElementOrParent>} [stopPredicate] - if the callback returns true than search will be stopped
     * @returns {MaybeHTMLElementOrParent}
     */
    closestByCond(
        searchPredicate: PredicateFn<HTMLElementOrParent>,
        stopPredicate: PredicateFn<HTMLElementOrParent>
    ): MaybeHTMLElementOrParent {
        return closestByCond(this, searchPredicate, stopPredicate);
    }

    nextByCond(searchPredicate: PredicateFn<Element>): MaybeHTMLElement {
        return nextByCond(this, searchPredicate);
    }

    previousByCond(searchPredicate: PredicateFn<Element>): MaybeHTMLElement {
        return previousByCond(this, searchPredicate);
    }

    /**
     * @ignore
     */
    @Deprecated(`Use HTMLElement.closest()`)
    closestTag(
        tagName: AllowedTagName,
        stopNode: Node
    ): MaybeHTMLElementOrParent {
        return closestTag(this, tagName, stopNode);
    }

    closestEventHolder(stopNode: Node): MaybeHTMLElementOrParent {
        return closestEventHolder(this, stopNode);
    }

    /**
     * @param {string} text
     * @param {boolean} [trusted] - Deprecated: use setUnsafeInnerHTML(). Defines if the text is trusted HTML
     * @returns {Element}
     */
    setInnerText(text: string, trusted?: boolean): Element {
        return setInnerText(this, text, trusted);
    }

    /**
     * @param {string} html
     * @returns {Element}
     */
    setUnsafeInnerHTML(html: string): Element {
        return setUnsafeInnerHTML(this, html);
    }

    /**
     * Creates element by given tagName and config.
     * @param {ElementConfig} config
     * @param {boolean} [trusted] - defines if element's strings are trusted HTML
     * @returns {HTMLElement}
     */
    createElement(config: ElementConfig, trusted?: boolean): HTMLElement {
        return createElement(config, trusted)!;
    }

    /**
     * Updates element by given config.
     * @param {ElementConfig} config
     * @param {boolean} [trusted] - defines if element's strings are trusted HTML
     * @returns {HTMLElement}
     */
    updateElement(config: ElementConfig, trusted?: boolean): Element {
        return updateElement(this, config, trusted);
    }

    /**
     * Inserts elements inside component.
     * @param {Array<ElementConfig | HTMLElement>} configs
     * @param {InsertPosition} [position]
     * @param {boolean} [trusted] - defines if element's strings are trusted HTML
     * @returns {Element}
     */
    insertElements(
        configs: Array<
            Optional<ElementConfig | HTMLElement | DocumentFragment | Text>
        >,
        position?: InsertPosition,
        trusted?: boolean
    ): Element {
        return insertElements(this, configs, position, trusted);
    }

    /**
     * Dispatches custom event.
     * @param {string} name - the event name
     * @param {unknown} [detail] - the event data object or value
     * @param {boolean} [bubbles] - enable event bubbling. Enabled by default
     * @param {boolean} [cancelable] - enable preventing default action. Enabled by default
     * @returns {CustomEvent}
     */
    dispatchCustomEvent(
        name: string,
        detail?: unknown,
        bubbles?: boolean,
        cancelable?: boolean
    ): CustomEvent<unknown> {
        return dispatchCustomEvent(this, name, detail, bubbles, cancelable);
    }

    /**
     * Dispatches native event.
     * @param {string} name - the event name
     * @param {boolean} [bubbles] - enable event bubbling. Enabled by default
     * @returns {Event}
     */
    dispatchNativeEvent(name: string, bubbles?: boolean): Event {
        return dispatchNativeEvent(this, name, bubbles);
    }

    /**
     * @param {string} key
     * @returns {Array<HTMLElement>}
     * @protected
     */
    protected getChildrenForSlot(key: string): Array<HTMLElement> {
        return getChildrenForSlot(this, key);
    }

    /**
     * @returns {Array<HTMLElement>}
     * @protected
     */
    protected detachChildNodes(): Array<HTMLElement> {
        return detachChildNodes(this);
    }

    /**
     * HTML parser needs to know the children's data.
     * So we will re-detach them to make browser renders them first.
     * @protected
     */
    protected redetachChildNodes(): void {
        redetachChildNodes(this);
    }

    /**
     * Finds direct children matched by condition.
     * @param {string} selector
     * @returns {HTMLElement[]}
     */
    queryChildren(selector: string): Array<HTMLElement> {
        return queryChildren(this, selector);
    }

    /**
     * Returns the node's position in the parent DOM tree.
     * @returns {number}
     */
    position(): number {
        return position(this);
    }

    /**
     * Add event listener for event to this node.
     * @deprecated use element.removeEventListener() instead.
     * @param {unknown[]} [args] - selector to the child element multiple identical
     * listeners to the same event
     * @example
     * Attach click event to the child element matched by selector ".submit-btn"
     * element.subscribe(".submit-btn", "click", function (event) {
     *   console.log("On child element clicked");
     * })
     * @returns {UIElement}
     */
    @Deprecated(`Use element.addEventListener().`)
    subscribe(...args: Array<unknown>): UIElement {
        let target;
        let eventName;
        let handler;
        let allowDuplication;

        if (
            args.length === 2 ||
            (args.length === 3 && typeof args[2] === 'boolean')
        ) {
            target = '';
            eventName = args[0] as string;
            handler = args[1] as EventHandler;
            allowDuplication = false;
        } else if (
            args.length === 4 ||
            (args.length === 3 && typeof args[2] === 'function')
        ) {
            target = args[0] as string;
            eventName = args[1] as string;
            handler = args[2] as EventHandler;
            allowDuplication = (args[3] as boolean) || false;
        } else {
            throw new Error('Wrong arguments passed to subscribe function');
        }

        const element = !target ? this : this.querySelectorAll(target);
        if (!element || (element instanceof NodeList && !element.length)) {
            return this;
        }
        const subscriptions = this.getSubscriptions(target, eventName);
        if (subscriptions.includes(handler) && !allowDuplication) {
            return this;
        }
        subscriptions.push(handler);

        [].forEach.call(
            element instanceof NodeList ? element : [element],
            (node: Node) => node.addEventListener(eventName, handler)
        );

        return this;
    }

    /**
     * Remove event listener from this node.
     * @deprecated use element.removeEventListener() instead.
     * @param {unknown[]} [args] - selector to the child element
     * @example
     * Detach the handler for click event from a child-element matched by selector
     * element.unsubscribe('.submit-btn', 'click', handler)
     * @returns {UIElement} reference to itself
     */
    @Deprecated(`Use element.removeEventListener() instead`)
    unsubscribe(...args: Array<string | EventHandler>): UIElement {
        let target: string;
        let eventName: string;
        let handler: EventHandler;

        if (args.length === 2) {
            target = '';
            eventName = args[0] as string;
            handler = args[1] as EventHandler;
        } else if (args.length === 3) {
            target = args[0] as string;
            eventName = args[1] as string;
            handler = args[2] as EventHandler;
        } else {
            throw new Error('Wrong arguments passed to unsubscribe function');
        }

        const element = !target ? this : this.querySelectorAll(target);
        if (!element || (element instanceof NodeList && !element.length)) {
            return this;
        }
        const subscriptions = this.getSubscriptions(target, eventName);
        const index = subscriptions.indexOf(handler);
        if (index > -1) {
            subscriptions.splice(index, 1);
        }

        [].forEach.call(
            element instanceof NodeList ? element : [element],
            (node: Node) => node.removeEventListener(eventName, handler)
        );
        return this;
    }

    /**
     * Remove all event listeners from this node.
     * @deprecated Use element.removeEventListener() instead
     * variation 1
     * @param {string} param1 - event name
     * @example
     * // detach all handlers for click event from a host element:
     * ```element.unsubscribeAll("click")```
     * @returns {UIElement}
     */
    /**
     * Remove all event listeners from this node.
     * @deprecated Use element.removeEventListener() instead
     * variation 2
     * @param {unknown[]} [args] - selector to the child element
     * @example
     * // detach all handlers for click event from a child element matched by selector
     * element.unsubscribe(".submit-btn", "click", handler)
     * @returns {UIElement}
     */
    @Deprecated(`Use element.removeEventListener() instead`)
    unsubscribeAll(...args: Array<string>): UIElement {
        let target: string;
        let eventName: string;

        if (args.length === 1) {
            target = '';
            eventName = args[0];
        } else if (args.length === 2) {
            target = args[0];
            eventName = args[1];
        } else {
            throw new Error(
                'Wrong arguments passed to unsubscribeAll function'
            );
        }

        const element = !target ? this : this.querySelectorAll(target);
        if (!element || (element instanceof NodeList && !element.length)) {
            return this;
        }
        const subscriptions = this.getSubscriptions(target, eventName);
        while (subscriptions.length) {
            const handler = subscriptions.shift();
            const collection =
                element instanceof NodeList ? element : [element];
            for (const item of collection) {
                item.removeEventListener(eventName, handler!);
            }
        }
        return this;
    }

    /**
     * Update a class list of the node by given config.
     * @example
     * // adds -active class to the node classList and removes -awesome from the node classList
     * element.updateClassList({"-active": true, "-awesome": false})
     * @param {Record<string, boolean>} config - hash map where key is classname value is flag
     *                                            to add / remove the it
     * @returns {UIElement | HTMLElement}
     */
    updateClassList(config: Record<string, boolean>): Element {
        return updateClassList(this, config);
    }

    /**
     * Update class list of the node by reverting given config,
     * could be used for animations to rollback last applied class config.
     * @example
     * // adds -active class to the node classList and removes -awesome from the node classList
     * element.revertClassList({"-active": true, "-awesome": false})
     * @param {Record<string, boolean>} config - hash map where key is classname value is flag
     * to add / remove it.
     * @returns {UIElement | HTMLElement}
     */
    @Deprecated(`Use UI.revertClassList();`)
    revertClassList(config: Record<string, boolean>): Element {
        return revertClassList(this, config);
    }

    /**
     * @param {Record<string, boolean>} config - hash map where key is classname value is flag
     * @param {number} timeout
     * @returns {Promise<void>}
     */
    @Deprecated(`Use UI.runTransition()`)
    runTransition(
        config: Record<string, boolean>,
        timeout: number
    ): Promise<void> {
        return runTransition(this, config, timeout);
    }

    /**
     * Recursively check attribute for validate() callback through UI parents
     * @param {string} name
     * @returns {AttributeValidationHandler | undefined}
     * @protected
     */
    protected findValidateCallback(
        name: string
    ): AttributeValidationHandler | void {
        if (this.constructor.name === UIElement.name) {
            return;
        }

        const attr = (this.constructor as typeof UIElement).props.attributes?.[
            name
        ];
        return attr
            ? (attr as PropsAttribute).validate
            : UIElement.prototype.findValidateCallback.call(
                  Object.getPrototypeOf(this.constructor.prototype),
                  name
              );
    }

    /**
     * Validate attribute's value if attribute has validate() callback
     * @param {string} name
     * @param {string} value
     */
    validateAttribute(name: string, value: string): void {
        const validate = this.findValidateCallback(name);
        typeof validate === 'function' && validate(this, value);
    }

    /**
     * A lifecycle hook called when element's attribute has been changed.
     * attributeChangedCallback should not be used directly. Use observeAttributes instead.
     * @param {string} name - attribute name
     * @param {string} oldValue - previous value
     * @param {string} newValue - current value
     */
    protected attributeChangedCallback(
        name: string,
        oldValue: string,
        newValue: string
    ): void {
        if (oldValue === newValue) {
            return;
        }
        if (
            UIElement.DEBUG_MODE &&
            this.hydrated &&
            this.hasAttribute(name) &&
            UIElement.propsToValidate?.includes(name)
        ) {
            this.validateAttribute(name, newValue);
        }
        const callback = this.observeAttributes.bind(
            this,
            name,
            oldValue,
            newValue
        );
        whenDOMReady(callback, true);
    }

    /**
     * Wrapper for attributeChangedCallback to use this dynamically on document state
     * and DOMContentLoaded. Should be used instead of attributeChangedCallback if needed.
     * @param {string} name - attribute name
     * @param {string} oldValue - previous value
     * @param {string} newValue - current value
     */
    observeAttributes(
        name: string,
        oldValue: AttributeValue,
        newValue: string | null
    ): void {
        /* nop */
    }

    /**
     * Register plugin for class.
     * @param {string} key
     * @param {object} PluginConstructor
     * @param {boolean} [autoload]
     */
    static registerPlugin(
        key: string,
        PluginConstructor: typeof ComponentPlugin,
        autoload = false
    ): void {
        if (!this.plugins) {
            this.plugins = {};
        }
        if (autoload) {
            if (!this.autoloadPlugins) {
                this.autoloadPlugins = {};
            }
            this.autoloadPlugins[key] = PluginConstructor;
        }
        this.plugins[key] = PluginConstructor;
        if (PluginConstructor.props) {
            this.defineAbstractElement(PluginConstructor.props);
        }
        if (isDOMReady()) {
            const tagName = String(this.is);
            const selector = autoload
                ? tagName
                : `${tagName}[plugins~="${key}"]`;
            document.querySelectorAll<UIElement>(selector).forEach((node) => {
                const plugin = node.getPlugin(key);
                if (plugin) {
                    node.instancePlugins[key] = plugin;
                    if (plugin?.render) {
                        plugin.render();
                    }
                    if (plugin?.hydrate) {
                        plugin.hydrate();
                    }
                }
            });
        }
    }

    /**
     * Un-register plugin for class.
     * @param {string} key
     */
    static unregisterPlugin(key: string): void {
        delete this.plugins[key];
        delete this.autoloadPlugins[key];
    }

    /**
     * Gets the plugin by key.
     * @param {string} key
     * @returns {ComponentPlugin | null}
     */
    getPlugin(key: string): ComponentPlugin | null {
        if (!this.instancePlugins) {
            this.instancePlugins = {};
        }
        if (this.instancePlugins[key]) {
            return this.instancePlugins[key];
        }
        if (!(this.constructor as typeof UIElement).plugins[key]) {
            return null;
        }
        const PluginConstructor = (this.constructor as typeof UIElement)
            .plugins[key];
        return new PluginConstructor(this);
    }

    /**
     * Gets all plugins for instance.
     * @returns {Record<string, string> | string}
     * @protected
     */
    protected getInstancePlugins(): Record<string, ComponentPlugin> {
        if (this.instancePlugins) {
            return this.instancePlugins;
        }

        this.instancePlugins = {};
        const autoPlugins = (this.constructor as typeof UIElement)
            .autoloadPlugins;
        if (autoPlugins) {
            Object.keys(autoPlugins).forEach((key) => {
                const PluginConstructor = (this.constructor as typeof UIElement)
                    .autoloadPlugins[key];
                this.instancePlugins[key] = new PluginConstructor(this);
            });
        }

        const pluginsStr = this.getAttribute('plugins');
        if (pluginsStr) {
            pluginsStr.split(/\s+/).forEach((key) => {
                if (!(this.constructor as typeof UIElement).plugins[key]) {
                    console.warn(
                        'Plugin ' +
                            key +
                            ' is not registered for class ' +
                            this.constructor.name
                    );
                    return;
                }
                const PluginConstructor = (this.constructor as typeof UIElement)
                    .plugins[key];
                this.instancePlugins[key] = new PluginConstructor(this);
            });
        }
        return this.instancePlugins;
    }

    /**
     * @param {string} key
     * @param {Record<string, TranslationLabels.Label>} [substitutions]
     * @returns {TranslationLabels.Label}
     */
    getLabel(
        key: string,
        substitutions: TranslationLabels.Substitutions = {}
    ): TranslationLabels.Label {
        return Labels.processLabel(
            (this.constructor as typeof UIElement).labels,
            key,
            substitutions
        );
    }

    /**
     * Mutates the host element by given config or snapshot of another node
     * @param {ElementConfig | Node} newNode
     * @returns {HTMLElement}
     */
    rebuild(newNode: ElementConfig | Node): HTMLElement {
        return rebuild(this, newNode) as HTMLElement;
    }

    /**
     * @param {DocumentFragment | Array<ElementConfig | Node>} elements
     * @param {SmoothRemoveCallback} [smoothRemove]
     * @param {SmoothAppendCallback} [smoothAppend]
     * @returns {HTMLElement}
     */
    rebuildChildren(
        elements: DocumentFragment | Array<ElementConfig | Node>,
        smoothRemove?: SmoothRemoveCallback,
        smoothAppend?: SmoothAppendCallback
    ): HTMLElement {
        return rebuildChildren(this, elements, smoothRemove, smoothAppend);
    }

    /**
     * Recursively pre-render Element tree.
     * @param {boolean} [recursive]
     */
    prerenderElementsTree(recursive?: boolean): void {
        prerenderElementsTree(this, recursive);
    }

    /**
     * Call a specific lifecycle hook for component and for it's plugins
     * @param {("render"|"hydrate"|"disconnect"|"reconnect")} hookName
     * @private
     */
    private runLifecycleHook(hookName: LifeCycleHook): void {
        const classConstructor = this.constructor as typeof UIElement;
        if (typeof this[hookName] === 'function') {
            this[hookName]();
        }
        if (!classConstructor.plugins) {
            return;
        }
        const plugins = this.getInstancePlugins();
        Object.keys(plugins).forEach((key) => {
            if (typeof plugins[key][hookName] === 'function') {
                plugins[key][hookName]?.();
            }
        });
    }

    /**
     * A lifecycle hook called each time after the component's
     * element is inserted into the document.
     * Do not override the base hook directly.
     * The "render/hydrate/reconnect" methods should be used instead
     * @private
     */
    private connectedCallback(): void {
        const render = (): void => {
            try {
                if (!this.rendered) {
                    this.runLifecycleHook('render');
                    this.state = ComponentState.Rendered;
                }
                if (!this.hydrated) {
                    this.runLifecycleHook('hydrate');
                    this.state = ComponentState.Hydrated;
                    this.dispatchEvent(new CustomEvent('hydrated'));
                    if (UIElement.DEBUG_MODE) {
                        (
                            this.constructor as typeof UIElement
                        ).propsToValidate?.forEach((name) => {
                            const attr = this.getAttribute(name);
                            if (attr !== null) {
                                this.validateAttribute(name, attr);
                            }
                        });
                    }
                } else {
                    this.runLifecycleHook('reconnect');
                }
            } catch (e) {
                console.log(this.constructor.name, this);
                console.error(e);
            }
            patchExternallyControlledFormProperties(
                this,
                UIElement.getPatchedControlElements()
            );
        };
        whenDOMReady(render, !UIElement.ASYNC_MODE);
    }

    /**
     * A lifecycle hook called each time after the component's
     * element is removed from the document.
     * Do not override the base hook directly the "disconnect" method should be used instead
     * @private
     */
    private disconnectedCallback(): void {
        if (this.hydrated) {
            this.runLifecycleHook('disconnect');
        }
        if (isCustomFormComponent(this)) {
            UIElement.patchedControlElements.delete(this.getControlElement());
        }
    }

    /**
     * This method should build initial markup, has to be overwritten in child class.
     * This method won't be called in runtime if the component was already pre-rendered.
     * Do not set local variables here.
     * Do not set any handlers here.
     */
    render(): void {
        /* nop */
    }

    /**
     * This method should hydrate existing markup, has to be overwritten in child class.
     * This method won't be called in prerendering step only for runtime.
     * You should set local variables here
     * You should set handlers here
     */
    hydrate(): void {
        /* nop */
    }

    /**
     * A lifecycle hook called when the component's
     * element is removed from the document.
     */
    disconnect(): void {
        /* nop */
    }

    /**
     * A lifecycle hook called when after the hydrated component's
     * element is attached to the DOM tree
     */
    reconnect(): void {
        /* nop */
    }
}
